import { ethers } from "ethers";
import { getAgentWallet, ensureAgentApprovedOnEngine } from "../../../utils/oneClick";

// Resolve the signer for a gasless spot order: the agent key when it's approved on the spot engine
// (auto-approving it on demand if a one-click session predates spot support), else the connected
// wallet. Avoids the "Unauthorized signer" relayer rejection for perp-only agents.
export async function resolveSpotSigner(
  account: string,
  chainId: number,
  spotEngine: string,
  spotDomainName: string,
  relayerUrl: string | undefined,
  provider: ethers.providers.Web3Provider,
  walletSigner: any
): Promise<any> {
  const agentWallet = getAgentWallet(account, chainId);
  if (!agentWallet || !relayerUrl) return walletSigner;
  try {
    const ok = await ensureAgentApprovedOnEngine({
      provider: provider as any,
      account,
      chainId,
      engineAddress: spotEngine,
      domainName: spotDomainName,
      nonceRouter: "agentspot",
      engineParam: "spot",
      relayerUrl,
    });
    return ok ? agentWallet : walletSigner;
  } catch {
    return walletSigner; // approval rejected/failed — fall back to the connected wallet
  }
}

// EIP-712 type for gasless spot cancellation (matches CLOBEngine CANCEL_SPOT_ORDER_TYPEHASH).
export const CANCEL_SPOT_ORDER_TYPES = {
  CancelSpotOrder: [
    { name: "user", type: "address" },
    { name: "orderBook", type: "address" },
    { name: "orderId", type: "uint48" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

export const ERC20_DECIMALS_ABI = [
  "function decimals() view returns (uint8)",
];

export const BALANCE_MANAGER_ABI = [
  "function getBalance(address user, address currency) external view returns (uint256)",
];

export const ORDERBOOK_QUOTE_ABI = [
  "function calculateOrderAmounts(uint128 price, uint128 quantity, uint8 side, uint8 orderType) external view returns (uint256 baseAmount, uint256 quoteAmount)",
];

// CLOBEngine/CLOBRouter ABI for order placement
export const CLOB_ENGINE_ORDER_ABI = [
  // placeOrder — requires funds already in BalanceManager
  "function placeOrder(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 price, uint128 quantity, uint8 side, address user) external returns (uint48)",
  // placeOrderWithDeposit — auto-deposits from wallet via transferFrom
  "function placeOrderWithDeposit(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 price, uint128 quantity, uint8 side, address user) external returns (uint48)",
  // placeMarketOrder — requires funds already in BalanceManager
  "function placeMarketOrder(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 quantity, uint8 side, address user, uint256 minAmountOut) external returns (uint48)",
  // placeMarketOrderWithDeposit — auto-deposits from wallet via transferFrom
  "function placeMarketOrderWithDeposit(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 quantity, uint8 side, address user, uint256 minAmountOut) external returns (uint48)",
  // cancelOrder — cancels from the connected user's wallet
  "function cancelOrder(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint48 orderId) external",
];

// Ultrafuzz M-1 layout — modular clob-dex chains (Arc) whose CLOBRouter market-order functions gained
// a trailing `maxQuoteIn` (caps quote spent on a BUY; type(uint256).max disables the cap). Limit-order
// and cancel signatures are unchanged, so this mirrors CLOB_ENGINE_ORDER_ABI except for the two market
// functions. (Monad's consolidated CLOBEngine adds an extra `deadline` param and is NOT this shape.)
export const CLOB_ENGINE_ORDER_ABI_V2 = [
  "function placeOrder(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 price, uint128 quantity, uint8 side, address user) external returns (uint48)",
  "function placeOrderWithDeposit(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 price, uint128 quantity, uint8 side, address user) external returns (uint48)",
  // placeMarketOrder — now takes maxQuoteIn as the trailing param
  "function placeMarketOrder(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 quantity, uint8 side, address user, uint256 minAmountOut, uint256 maxQuoteIn) external returns (uint48)",
  // placeMarketOrderWithDeposit — now takes maxQuoteIn as the trailing param
  "function placeMarketOrderWithDeposit(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint128 quantity, uint8 side, address user, uint256 minAmountOut, uint256 maxQuoteIn) external returns (uint48)",
  "function cancelOrder(tuple(address baseCurrency, address quoteCurrency, address orderBook) pool, uint48 orderId) external",
];

export const ERC20_APPROVE_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
];

// EIP-712 type for a gasless market order (matches CLOBEngine PLACE_MARKET_ORDER_TYPEHASH).
// Legacy layout — chains still on the pre-PR#18 CLOBRouter (Arc, dogeos, bnb).
export const PLACE_MARKET_ORDER_TYPES = {
  PlaceMarketOrder: [
    { name: "user", type: "address" },
    { name: "orderBook", type: "address" },
    { name: "quantity", type: "uint128" },
    { name: "side", type: "uint8" },
    { name: "minAmountOut", type: "uint256" },
    { name: "builder", type: "address" },
    { name: "builderFee", type: "uint16" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

// Post-PR#18 layout — CLOBEngine gained `maxQuoteIn` (caps quote spent on a market BUY). Monad only.
// Field order MUST match the on-chain PLACE_MARKET_ORDER_TYPEHASH.
export const PLACE_MARKET_ORDER_TYPES_V2 = {
  PlaceMarketOrder: [
    { name: "user", type: "address" },
    { name: "orderBook", type: "address" },
    { name: "quantity", type: "uint128" },
    { name: "side", type: "uint8" },
    { name: "minAmountOut", type: "uint256" },
    { name: "maxQuoteIn", type: "uint256" },
    { name: "builder", type: "address" },
    { name: "builderFee", type: "uint16" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

// EIP-712 type for a gasless limit order (matches CLOBEngine PLACE_SPOT_ORDER_TYPEHASH).
export const PLACE_SPOT_ORDER_TYPES = {
  PlaceSpotOrder: [
    { name: "user", type: "address" },
    { name: "orderBook", type: "address" },
    { name: "price", type: "uint128" },
    { name: "quantity", type: "uint128" },
    { name: "side", type: "uint8" },
    { name: "timeInForce", type: "uint8" },
    { name: "builder", type: "address" },
    { name: "builderFee", type: "uint16" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};
